import { type ComponentPropsWithoutRef, useId } from "react";

import { Label } from "./Label";
import { Description } from "./Description";
import { HIDDEN_INPUT, BOX_BASE, INVALID_BOX } from "./customControlStyles";
import { ErrorMessage, type ErrorKey } from "./ErrorMessage";

interface RadioProps
  extends Omit<
    ComponentPropsWithoutRef<"input">,
    "defaultValue" | "id" | "name" | "type" | "value"
  > {
  id?: string;
  label: string;
  name: string;
  options: string[];
  value?: string;
  defaultValue?: string;
  description?: string;
  error?: ErrorKey;
}

export const Radio = ({
  label,
  description,
  error,
  id,
  name,
  options,
  required,
  value,
  defaultValue,
  "aria-describedby": ariaDescribedBy,
  ...radioProps
}: RadioProps) => {
  const generatedId = useId();
  const groupId = id ?? generatedId;
  const invalid = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5">
      <fieldset id={groupId}>
        <legend>
          <Label htmlFor={groupId} label={label} required={required} />
        </legend>

        {options.map((option, index) => {
          const optionId = `${groupId}-option-${index}`;

          return (
            <div key={option} className="group flex items-center gap-3">
              <span className="relative flex">
                <input
                  {...radioProps}
                  id={optionId}
                  type="radio"
                  name={name}
                  value={option}
                  required={required}
                  checked={value === undefined ? undefined : value === option}
                  defaultChecked={
                    defaultValue === undefined
                      ? undefined
                      : defaultValue === option
                  }
                  aria-describedby={ariaDescribedBy}
                  className={HIDDEN_INPUT}
                />
                <span
                  aria-hidden
                  className={[
                    BOX_BASE,
                    "rounded-full",
                    "peer-checked:border-primary-500 peer-checked:bg-tertiary-50",
                    "peer-checked:[&>span]:scale-100 peer-checked:[&>span]:opacity-100",
                    invalid ? INVALID_BOX : "",
                  ].join(" ")}
                >
                  <span className="size-[6px] scale-0 rounded-full bg-primary-500 opacity-0 transition duration-150" />
                </span>
              </span>
              <label
                htmlFor={optionId}
                className="text-sm leading-5 font-medium group-has-[input:disabled]:cursor-not-allowed group-has-[input:disabled]:text-tertiary-600"
              >
                {option}
              </label>
            </div>
          );
        })}
      </fieldset>

      {error && <ErrorMessage error={error} />}
      {description && !error && <Description description={description} />}
    </div>
  );
};
